import { TRAINING } from '../content';
import { visibleSections, allows, type TrainingViewer } from './access';

/**
 * Every topic a given reader is expected to COMPLETE — foundations plus a track.
 *
 * ⚠️ ONE DEFINITION, used by BOTH the reader's own "N of M done" count and the
 * admin roll-up's denominator. They were written separately and drifted, which
 * is what #283 was: ticking a card moved neither number. Two copies of "what
 * should this person have read" is one copy too many.
 *
 * ⚠️ THE APPENDIX IS NOT COUNTED, and this is a deliberate REVERSAL of #283.
 * That fix read the mismatch the other way round — the appendix rendered ticks,
 * so it was added to the denominator to make the counts agree. The trouble is
 * that nothing ever walks a reader through it: guided mode excludes all 31
 * cards on purpose (see guided.ts — "31 Next clicks through a reference is not
 * a walkthrough"), so finishing every track still left a third of the
 * denominator untouched and the roll-up read 74% for someone who had genuinely
 * finished (product owner, 2026-08-18: "I completed all training tracks but it's showing
 * I am only 74%").
 *
 * Of the two ways to make those agree, this is the one that matches what the
 * appendix IS. It answers "what is this screen?" for someone who has landed on
 * one; you look things up in it, you do not complete it. So it carries notes
 * but no tick (TrainingPage renders it `reference`), and it is absent here.
 * If it is ever put back, the tick has to come back with it — a tick that moves
 * no counter is precisely the thing #283 was raised about.
 *
 * @param v      the reader being evaluated
 * @param trackId the ONE track that counts. Omitting it counts no track at all
 *                (foundations only) — it does NOT mean "every track they could
 *                open", which is the other half of the same 74% report: every
 *                track but the Administrator one is open to everybody, so a PM
 *                was being measured against the estimator, PE, ops and field
 *                leader material as well as their own.
 */
export function targetTopics(v: TrainingViewer, trackId?: string): Set<string> {
  const ids = new Set<string>();

  const named = trackId ? TRAINING.tracks.find((t) => t.id === trackId) : undefined;
  const track = named && allows(v, named.access) ? named : undefined;

  // ⚠️ THE SCOPE IS RESOLVED BEFORE ANYTHING IS COUNTED, FOUNDATIONS INCLUDED.
  // The Field Leader track is scoped by TRACK, not by reader, and that scope
  // covers the foundations too — TrainingPage and guidedScreens both force it
  // for both parts. Forcing it only around the track loop (which is what this
  // did) is invisible while the caller pre-forces the viewer, as TrainingPage
  // does, and wrong the moment one doesn't: the admin roll-up passes the
  // reader's own unforced viewer, so someone holding BOTH FieldLeader and an
  // office grant like `projects.edit` kept the `hideFromFieldLeaderOnly`
  // foundation topics in their denominator while their actual page and
  // walkthrough hid them — a target larger than anything they can tick
  // (Codex, #351).
  const tv: TrainingViewer = track?.id === 'field-leader' ? { ...v, forceFieldLeaderScope: true } : v;

  for (const s of visibleSections(tv, TRAINING.foundations)) for (const t of s.topics) ids.add(t.id);
  if (track) for (const s of visibleSections(tv, track.sections)) for (const t of s.topics) ids.add(t.id);

  return ids;
}

/** Is this topic still part of the guide? Used to keep notes on topics that
 *  aren't in someone's target (an appendix card, another role's track) from
 *  silently vanishing from the manager view, while still dropping notes left
 *  on topics that have since been deleted. */
export function topicExists(topicId: string): boolean {
  for (const s of TRAINING.foundations) if (s.topics.some((t) => t.id === topicId)) return true;
  for (const tr of TRAINING.tracks) for (const s of tr.sections) if (s.topics.some((t) => t.id === topicId)) return true;
  for (const m of TRAINING.modules) if (m.topics.some((t) => t.id === topicId)) return true;
  return false;
}
